import { withAuthenticationRequired } from '@auth0/auth0-react'
import React, { useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { PageLoader } from 'app/views/components/layout/page-loader'
import { useHasPermission } from 'app/hooks/useHasPermission'

interface ProtectedRouteProps {
  component: React.ComponentType<any>
  permissionRequired?: string
  [key: string]: any
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component,
  permissionRequired,
  ...rest
}) => {
  // Memoize the wrapped component so its reference is stable across re-renders.
  // Without this, nested-route reconciliation would unmount the child on every
  // parent re-render (e.g. tab switch), defeating the point of nested routes.
  const Component = useMemo(
    () =>
      withAuthenticationRequired(component, {
        onRedirecting: () => (
          <div className="page-layout">
            <PageLoader />
          </div>
        ),
      }),
    [component],
  )

  if (permissionRequired) {
    return (
      <PermissionGate permissionRequired={permissionRequired}>
        <Component {...rest} />
      </PermissionGate>
    )
  }

  return <Component {...rest} />
}

interface PermissionGateProps {
  permissionRequired: string
  children: React.ReactNode
}

const PermissionGate: React.FC<PermissionGateProps> = ({ permissionRequired, children }) => {
  const hasPermission = useHasPermission(permissionRequired)
  if (!hasPermission) return <Navigate to="/mywork" replace={true} />
  return <>{children}</>
}
